import React, { useState } from 'react';
import {
  Map,
  ZoomIn,
  ExternalLink,
  ShieldCheck,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { YouTubeEmbed } from '../YouTubeEmbed';

// =====================================================================
// Source officielle : Sécurité Routière (gouv.fr)
// URL : https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire
//       /permis-moto-categorie/passer-le-permis-a1-ou-a2
// Image : capdefpouns2.png (téléchargée et servie en local)
// =====================================================================

// Code couleur officiel (légende du schéma gouv.fr)
const LEGEND = [
  { color: '#7dd3fc', label: 'Déplacement sans aide du moteur' },
  { color: '#86efac', label: 'Allure réduite sans passager' },
  { color: '#fde68a', label: 'Freinage' },
  { color: '#3b82f6', label: 'Allure réduite avec passager' },
  { color: '#dc2626', label: 'Slalom — Évitement' },
];

// Les 6 manœuvres dans l'ordre exact de l'enchaînement du parcours
// (texte officiel sécurité-routière.gouv.fr)
const MANOEUVRES = [
  {
    n: 1,
    color: '#7dd3fc',
    title: 'Déplacer la moto sans l\'aide du moteur',
    steps: [
      'Débéquiller le motocycle (béquille centrale ou latérale)',
      'Pousser la moto en marche avant jusqu\'au franchissement complet par le motocycle de la porte de cônes',
      'Reculer la moto en marche arrière jusqu\'au franchissement complet par le motocycle de la porte de cônes',
      'Béquiller le motocycle (béquille centrale ou latérale)',
    ],
  },
  {
    n: 2,
    color: '#86efac',
    title: 'Déplacer la moto à allure réduite sans passager',
    steps: [
      'Effectuer le parcours de (1) vers (2) ; si le motocycle est équipé d\'une boîte manuelle, le temps est chronométré de (1) vers (2)',
      'Marquer un temps d\'arrêt en (4)',
    ],
  },
  {
    n: 3,
    color: '#fde68a',
    title: 'Effectuer un freinage d\'urgence',
    steps: [
      'En (4), démarrer en courbe et effectuer un demi-tour conformément au tracé',
      'En bout de piste, effectuer un demi-tour avant de revenir en ligne droite conformément au schéma',
      'Sur le retour en ligne droite, engager le 3ᵉ rapport de vitesse (sauf boîte automatique) et atteindre la vitesse de 50 km/h minimum à hauteur de la ligne C6 au plus tard',
      'À hauteur de la ligne C6, effectuer un freinage d\'urgence et immobiliser le motocycle à hauteur de la ligne C5 (piste sèche) ou C4 (piste humide)',
    ],
  },
  {
    n: 4,
    color: '#3b82f6',
    title: 'Déplacer la moto à allure réduite avec passager',
    steps: [
      'Effectuer avec le passager le parcours conformément au schéma',
      'Déposer le passager dans la zone comprise entre la porte « piquets » et la porte « cônes » conformément au schéma',
    ],
  },
  {
    n: 5,
    color: '#dc2626',
    title: 'Effectuer un slalom',
    steps: [
      'Engager le 3ᵉ rapport de vitesse au plus tard au niveau du 1ᵉʳ cône du slalom conformément au schéma',
      'Atteindre la vitesse de 40 km/h au plus tard au niveau de la ligne C7 conformément au schéma',
      'En bout de piste, effectuer un demi-tour avant de revenir en ligne droite conformément au schéma',
    ],
  },
  {
    n: 6,
    color: '#dc2626',
    title: 'Effectuer un évitement',
    steps: [
      'Sur le retour en ligne droite, engager le 3ᵉ rapport de vitesse (sauf boîte automatique) avant l\'entrée dans le couloir d\'évitement et atteindre la vitesse de 50 km/h minimum à hauteur de la ligne C6 au plus tard',
      'Effectuer un évitement conformément au schéma',
      'Après l\'évitement, immobiliser le motocycle dans la zone dédiée conformément au schéma',
    ],
  },
];

const VIDEO_TUTORIALS = [
  {
    videoId: 'QZyx7EVX3nU',
    title: 'Plateau permis moto 2026 — Parcours n°1',
    channel: 'rudymotopro',
    channelHandle: '@rudymotopro',
    duration: '3:35',
  },
  {
    videoId: 't8pa26DSXWg',
    title: 'Plateau permis moto 2026 — Parcours n°2',
    channel: 'rudymotopro',
    channelHandle: '@rudymotopro',
    duration: '3:27',
  },
];

export function PlateauInteractive() {
  const [zoomOpen, setZoomOpen] = useState(false);
  const [activeId, setActiveId] = useState(1);
  const active = MANOEUVRES.find((m) => m.n === activeId) || MANOEUVRES[0];

  return (
    <section
      className="space-y-4"
      data-testid="plateau-interactive"
      aria-labelledby="plateau-interactive-title"
    >
      {/* En-tête éditorial */}
      <div className="mc-card relative overflow-hidden p-5 sm:p-6">
        <div
          className="pointer-events-none absolute -bottom-16 -left-12 w-48 h-48 rounded-full blur-3xl opacity-15"
          style={{ background: 'hsl(var(--primary))' }}
        />
        <div className="relative">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[hsl(var(--primary))] mb-1.5 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Source officielle · Sécurité Routière (gouv.fr)
          </div>
          <h2
            id="plateau-interactive-title"
            className="text-xl sm:text-2xl font-black tracking-tight leading-tight"
          >
            Le parcours plateau (hors circulation)
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 max-w-3xl leading-relaxed">
            <strong>10 minutes</strong> · parcours unique de <strong>6 manœuvres qui s'enchaînent</strong>.
            Schéma et descriptions extraits du site officiel <em>securite-routiere.gouv.fr</em>.
          </p>
        </div>
      </div>

      {/* TUTORIELS VIDÉO — embed nocookie + façade lite-youtube (RGPD-friendly) */}
      {VIDEO_TUTORIALS.length > 0 && (
        <section
          className="space-y-2"
          data-testid="plateau-video-tutorials"
          aria-labelledby="plateau-videos-title"
        >
          <div className="flex items-end justify-between gap-3 px-1">
            <div>
              <div
                id="plateau-videos-title"
                className="text-[10px] font-black uppercase tracking-widest text-muted-foreground"
              >
                Tutoriels vidéo
              </div>
              <p className="text-[11px] text-muted-foreground/75 mt-0.5">
                Vidéos de la chaîne <strong>@rudymotopro</strong> · lecture en mode privacy-enhanced (aucun cookie tant que tu n'appuies pas sur Play).
              </p>
            </div>
            <a
              href="https://youtube.com/@rudymotopro"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1 text-[11px] font-bold text-muted-foreground hover:text-foreground transition-colors"
              data-testid="plateau-channel-link"
            >
              Voir la chaîne <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {VIDEO_TUTORIALS.map((v) => (
              <YouTubeEmbed
                key={v.videoId}
                videoId={v.videoId}
                title={v.title}
                channel={v.channel}
                channelHandle={v.channelHandle}
                duration={v.duration}
                testId={`plateau-yt-${v.videoId}`}
              />
            ))}
          </div>
        </section>
      )}

      {/* SCHÉMA OFFICIEL — Image gouv.fr */}
      <div className="mc-card overflow-hidden" data-testid="plateau-official-schema">
        <button
          type="button"
          onClick={() => setZoomOpen(true)}
          className="group block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] focus-visible:ring-offset-2"
          aria-label="Agrandir le schéma officiel"
        >
          <div className="relative bg-[hsl(var(--muted))]">
            <picture>
              <source srcSet="/plateau/parcours-officiel.webp" type="image/webp" />
              <img
                src="/plateau/parcours-officiel.png"
                alt="Schéma officiel du parcours plateau du permis moto A1/A2 — Sécurité Routière"
                width="1460"
                height="625"
                className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.01]"
              />
            </picture>
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-sm rounded-full px-2.5 py-1 text-[11px] font-bold text-foreground/80 opacity-0 group-hover:opacity-100 transition-opacity">
              <ZoomIn className="w-3.5 h-3.5" /> Agrandir
            </div>
          </div>
        </button>

        {/* Légende code couleur */}
        <div
          className="p-4 border-t flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px]"
          data-testid="plateau-legend"
        >
          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mr-1">
            Légende
          </span>
          {LEGEND.map((l) => (
            <span key={l.label} className="inline-flex items-center gap-1.5">
              <span
                className="inline-block w-5 h-1 rounded-full"
                style={{ background: l.color }}
                aria-hidden="true"
              />
              <span>{l.label}</span>
            </span>
          ))}
        </div>
      </div>

      {/* SÉLECTEUR + DÉTAIL DES 6 MANŒUVRES */}
      <div className="grid lg:grid-cols-12 gap-3" data-testid="plateau-manoeuvres">
        {/* Sélecteur (gauche) */}
        <div className="lg:col-span-4 space-y-2" role="tablist">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1 px-1">
            Les 6 manœuvres dans l'ordre
          </div>
          {MANOEUVRES.map((m) => {
            const isActive = m.n === activeId;
            return (
              <button
                key={m.n}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveId(m.n)}
                className={`mc-card relative w-full text-left p-3 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                  isActive ? 'shadow-lg' : 'opacity-75 hover:opacity-100'
                }`}
                style={{
                  borderColor: isActive ? m.color : undefined,
                  boxShadow: isActive ? `0 0 0 2px ${m.color}` : undefined,
                }}
                data-testid={`plateau-manoeuvre-${m.n}`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="flex-shrink-0 w-8 h-8 rounded-full grid place-items-center font-black text-[12px] text-white"
                    style={{ background: m.color }}
                  >
                    {m.n}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-[13px] leading-tight tracking-tight">
                      {m.title}
                    </div>
                    <div className="text-[10.5px] font-bold uppercase tracking-widest text-muted-foreground mt-0.5">
                      {m.steps.length} étape{m.steps.length > 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Détail manœuvre active (droite) */}
        <div className="lg:col-span-8" data-testid="plateau-manoeuvre-detail">
          <div className="mc-card p-5">
            <div
              className="text-[10px] font-black uppercase tracking-[0.18em] mb-2"
              style={{ color: active.color }}
            >
              Manœuvre {active.n} sur 6
            </div>
            <h3 className="font-black text-lg sm:text-xl leading-tight tracking-tight mb-4">
              {active.title}
            </h3>
            <ol className="space-y-2.5">
              {active.steps.map((s, i) => (
                <li
                  key={i}
                  className="flex gap-3 text-[13.5px] leading-relaxed text-foreground/85"
                >
                  <span
                    className="flex-shrink-0 w-6 h-6 rounded-full grid place-items-center font-black text-[11px] mt-0.5"
                    style={{
                      background: `${active.color}22`,
                      color: active.color,
                    }}
                  >
                    {i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* Lightbox / agrandissement */}
      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent
          className="max-w-6xl w-[96vw] p-0 overflow-hidden bg-background"
          data-testid="plateau-zoom-dialog"
        >
          <DialogTitle className="sr-only">
            Schéma officiel du parcours plateau — Sécurité Routière
          </DialogTitle>
          <DialogDescription className="sr-only">
            Schéma haute résolution du parcours hors circulation du permis moto
          </DialogDescription>
          <div className="relative bg-[hsl(var(--muted))] flex items-center justify-center p-2">
            <picture>
              <source srcSet="/plateau/parcours-officiel.webp" type="image/webp" />
              <img
                src="/plateau/parcours-officiel.png"
                alt="Schéma officiel agrandi du parcours plateau"
                className="w-full h-auto max-h-[88vh] object-contain"
              />
            </picture>
          </div>
        </DialogContent>
      </Dialog>

      {/* Crédit source */}
      <div className="mc-card p-3 text-[11px] text-muted-foreground leading-relaxed flex flex-wrap items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 text-[hsl(var(--primary))]" />
        <span>
          <strong className="text-foreground/80">Source officielle :</strong>{' '}
          <a
            href="https://www.securite-routiere.gouv.fr/les-differents-permis-de-conduire/permis-moto-categorie/passer-le-permis-a1-ou-a2"
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-dotted hover:text-foreground"
          >
            sécurité-routière.gouv.fr
          </a>
          {' '}— Schéma et déroulé des 6 manœuvres conformes au programme officiel ETM
          (arrêté du 23 avril 2012, REMC).
        </span>
      </div>
    </section>
  );
}

export default PlateauInteractive;
